import { environment } from '@/config/environment'
import { iamAuth } from '@/lib/firebase'

export interface ImportSummary {
  totalAccounts: number
  totalCategories: number
  totalTransactions: number
  totalPayees: number
  dateRange?: { earliest?: string; latest?: string }
}

export interface ImportPreview {
  importId: string
  fileName: string
  summary: ImportSummary
  preview: unknown
  suggestions: {
    categoryMappings?: Record<string, string>
    accountMappings?: Record<string, string>
    payeeMappings?: Record<string, string>
  }
  existingCategories: Array<{ id: string; name: string }>
  existingAccounts: Array<{ id: string; name: string; type?: string }>
}

export interface ImportResult {
  success: boolean
  imported: number
  skipped: number
  failed: number
  errors: string[]
  createdCategories: string[]
  createdAccounts: string[]
}

async function authHeader(): Promise<HeadersInit> {
  const token = await iamAuth.currentUser?.getIdToken()
  if (!token) throw new Error('Not signed in')
  return { Authorization: `Bearer ${token}` }
}

export async function uploadYnabFile(file: File): Promise<ImportPreview> {
  const form = new FormData()
  form.append('file', file)
  form.append('source', 'ynab')
  const res = await fetch(`${environment.apiBaseURL}/import/upload`, {
    method: 'POST',
    headers: await authHeader(),
    body: form,
  })
  if (!res.ok) {
    const body = await res.text().catch(() => '')
    throw new Error(`Upload failed (HTTP ${res.status}): ${body}`)
  }
  return (await res.json()) as ImportPreview
}

export async function executeImport(
  importId: string,
  preview: ImportPreview,
): Promise<ImportResult> {
  const res = await fetch(`${environment.apiBaseURL}/import/execute`, {
    method: 'POST',
    headers: { ...(await authHeader()), 'Content-Type': 'application/json' },
    body: JSON.stringify({
      importId,
      categoryMappings: preview.suggestions.categoryMappings ?? {},
      accountMappings: preview.suggestions.accountMappings ?? {},
      payeeMappings: preview.suggestions.payeeMappings ?? {},
      options: {
        createMissingCategories: true,
        createMissingAccounts: true,
        duplicateHandling: 'skip',
      },
    }),
  })
  if (!res.ok) {
    const body = await res.text().catch(() => '')
    throw new Error(`Import failed (HTTP ${res.status}): ${body}`)
  }
  return (await res.json()) as ImportResult
}
